import React, { useState } from 'react';
import {
  Avatar,
  Button,
  TextField,
  Paper,
  Box,
  Grid,
  Typography,
  Snackbar,
  Alert,
  CssBaseline,
  useMediaQuery,
  CircularProgress,
  Grow
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import axiosInstance from '../../api/axiosConfig';
import theme from '../../theme'; // Import the shared theme


const ForgotPasswordPage = () => {
  const [code, setCode] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!code.trim()) {
      handleSnackbar('Please enter your employee code.', 'warning');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosInstance.post('/api/employees/reset-password-request', {
        code: code.trim(),
      });

      if (response.status === 200) {
        handleSnackbar('Password reset email sent. Please check your inbox.', 'success');
      } else {
        handleSnackbar('An unexpected error occurred. Please try again.', 'error');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        const { message } = err.response.data;
        handleSnackbar(message || 'An error occurred. Please try again.', 'error');
      } else {
        handleSnackbar('Network error. Please check your connection.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    if (snackbarSeverity === 'success') {
      navigate('/login');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Grid
        container
        component="main"
        sx={{
          minHeight: '100vh',
          background: theme.palette.background.default, // Light grey background from theme
          padding: theme.spacing(4), // Add some padding around the entire page
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: isMobile ? theme.spacing(8) : theme.spacing(12),
        }}
      >
        {/* Grow transition for a subtle entrance animation */}
        <Grow in timeout={800}>
          <Grid
            item
            xs={12}
            sm={8}
            md={6}
            lg={4}
            component={Paper}
            elevation={4} // Reduced elevation
            sx={{
              padding: theme.spacing(4), // Padding inside the paper
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Avatar sx={{ bgcolor: 'primary.main', mb: 2 }}>
              <LockResetIcon fontSize="large" />
            </Avatar>
            <Typography component="h1" variant="h5">
              Forgot Password
            </Typography>
            <br></br>
            <Typography variant="body2">
              Please enter your employee code. We will send you a link to reset your password.
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, width: '100%' }}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="code"
                label="Employee Code"
                name="code"
                autoComplete="code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 2, py: 1.5, fontSize: '1rem' }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
              </Button>
              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    onClick={() => navigate('/login')}
                    variant="text"
                    size="small"
                  >
                    Back to Sign In
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grow>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
            elevation={6}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </ThemeProvider>
  );
};

export default ForgotPasswordPage;